import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../firebase';
import { collection, query, where, onSnapshot, orderBy, doc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';

// The four services Tiana checks off for every client, in display order.
// `profile` matches the label stored in the client's `services` array so we can
// gray out columns the client hasn't signed up for.
const SERVICES = [
  { key: 'reportSummary', label: 'Report Summary', profile: 'Report Summary' },
  { key: 'pressRelease',  label: 'Press Release',  profile: 'Press Release' },
  { key: 'socialPosts',   label: 'Social Posts',   profile: 'Social Posts' },
  { key: 'videos',        label: 'Videos',         profile: 'Video Generation' },
];

function monthKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function monthLabel(key) {
  const [y, m] = key.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

// Current month + the previous 11, newest first.
function recentMonths() {
  const out = [];
  const d = new Date();
  d.setDate(1);
  for (let i = 0; i < 12; i++) {
    out.push(monthKey(d));
    d.setMonth(d.getMonth() - 1);
  }
  return out;
}

function fmtStamp(ts) {
  if (!ts) return '';
  try {
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      + ', ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  } catch { return ''; }
}

export default function Dashboard() {
  const [clients, setClients] = useState([]);
  const [month, setMonth] = useState(monthKey(new Date()));
  const [status, setStatus] = useState({}); // clientId -> { reportSummary: {done, at}, ... }
  const [savingCell, setSavingCell] = useState(null); // `${clientId}:${key}`
  const [scheduled, setScheduled] = useState([]); // open custom-project tasks
  const [followupsReady, setFollowupsReady] = useState(0); // keyword follow-ups awaiting review

  // Active clients (every client gets all four services)
  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'clients'), orderBy('businessName')),
      snap => setClients(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(c => !c.isArchived)),
      err => console.error('clients snapshot error:', err)
    );
    return unsub;
  }, []);

  // Service completion status for the selected month
  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'serviceStatus'), where('month', '==', month)),
      snap => {
        const map = {};
        snap.docs.forEach(d => { map[d.data().clientId] = d.data(); });
        setStatus(map);
      },
      err => console.error('serviceStatus snapshot error:', err)
    );
    return unsub;
  }, [month]);

  // Scheduled one-off tasks (created on the Custom Projects page)
  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'tasks'), where('source', '==', 'custom-project')),
      snap => setScheduled(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(t => !t.completed)),
      err => console.error('scheduled tasks snapshot error:', err)
    );
    return unsub;
  }, []);

  // Keyword follow-ups ready for review — badge linking to the Follow-Ups tab
  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'keywordFollowups'), where('status', '==', 'ready_for_review')),
      snap => setFollowupsReady(snap.size),
      err => console.error('keywordFollowups snapshot error:', err)
    );
    return unsub;
  }, []);

  const isDone = (clientId, key) => !!status[clientId]?.[key]?.done;

  // Whether a client has signed up for the service backing a given column.
  // Legacy clients with no `services` array are treated as having everything.
  const hasService = (client, profile) =>
    !Array.isArray(client.services) || client.services.includes(profile);

  const toggle = async (clientId, key) => {
    const cellId = `${clientId}:${key}`;
    setSavingCell(cellId);
    const done = isDone(clientId, key);
    try {
      await setDoc(
        doc(db, 'serviceStatus', `${clientId}_${month}`),
        {
          clientId,
          month,
          [key]: done
            ? { done: false, at: null }
            : { done: true, at: serverTimestamp() },
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    } catch (e) {
      console.error('Failed to update service status:', e);
      alert('Could not save — please try again.');
    } finally {
      setSavingCell(null);
    }
  };

  // Numerator: done + applicable. Denominator: clients signed up for the service.
  const colCount = (s) => clients.filter(c => hasService(c, s.profile) && isDone(c.id, s.key)).length;
  const colTotal = (s) => clients.filter(c => hasService(c, s.profile)).length;

  const clearScheduled = async (taskId) => {
    try {
      await deleteDoc(doc(db, 'tasks', taskId));
    } catch (e) {
      console.error('Failed to clear task:', e);
    }
  };

  // --- Scheduled tasks: group by project, bucket by due date ---
  const getDue = (t) => t.dueDate?.toDate ? t.dueDate.toDate() : t.dueDate ? new Date(t.dueDate) : null;
  const startToday = new Date(); startToday.setHours(0, 0, 0, 0);
  const in7 = new Date(startToday); in7.setDate(in7.getDate() + 7);

  const schedGroupsMap = {};
  scheduled.forEach(t => {
    const key = t.projectId || t.id;
    if (!schedGroupsMap[key]) schedGroupsMap[key] = { key, title: t.title, notes: t.instructions, due: getDue(t), items: [] };
    schedGroupsMap[key].items.push(t);
  });
  const schedGroups = Object.values(schedGroupsMap)
    .map(g => ({ ...g, items: g.items.sort((a, b) => (a.clientName || '').localeCompare(b.clientName || '')) }))
    .sort((a, b) => (a.due?.getTime() || Infinity) - (b.due?.getTime() || Infinity));

  const bucketOf = (due) => {
    if (!due) return 'Later';
    const d = new Date(due); d.setHours(0, 0, 0, 0);
    if (d < startToday) return 'Overdue';
    if (d.getTime() === startToday.getTime()) return 'Today';
    if (d <= in7) return 'This week';
    return 'Later';
  };
  const BUCKETS = ['Overdue', 'Today', 'This week', 'Later'];
  const BUCKET_COLOR = { Overdue: '#f87171', Today: '#4ade80', 'This week': 'var(--text-secondary)', Later: 'var(--text-muted)' };
  const bucketed = BUCKETS.map(b => ({ name: b, groups: schedGroups.filter(g => bucketOf(g.due) === b) })).filter(b => b.groups.length > 0);

  const fmtDueShort = (d) => d
    ? d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
    : 'No date';

  const GRID = '1fr repeat(4, 150px)';

  return (
    <div className="page" style={{ maxWidth: 1100 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>Dashboard</h1>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            Monthly completion tracker — check off each service as it's done for every client
          </p>
        </div>
        <select
          value={month}
          onChange={e => setMonth(e.target.value)}
          style={{ background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 7, padding: '8px 12px', fontSize: 13, color: 'var(--text-primary)', outline: 'none' }}
        >
          {recentMonths().map(k => <option key={k} value={k}>{monthLabel(k)}</option>)}
        </select>
      </div>

      {/* Keyword follow-ups awaiting review */}
      {followupsReady > 0 && (
        <Link
          to="/keyword-followups"
          style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, padding: '12px 18px', borderRadius: 10, background: 'rgba(79,70,229,.10)', border: '1px solid #4f46e544', textDecoration: 'none' }}
        >
          <span style={{ fontSize: 12, fontWeight: 700, color: '#fff', background: '#4f46e5', borderRadius: 20, padding: '2px 10px' }}>{followupsReady}</span>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#818cf8' }}>
            keyword follow-up{followupsReady === 1 ? '' : 's'} ready for review
          </span>
          <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 'auto' }}>Open Follow-Ups →</span>
        </Link>
      )}

      {/* Scheduled one-off tasks — the at-a-glance reminder (calendar replacement) */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-title" style={{ marginBottom: 14 }}>
          Scheduled Tasks
          {scheduled.length > 0 && <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--text-muted)', marginLeft: 8 }}>{scheduled.length} open</span>}
        </div>
        {bucketed.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', padding: '12px 0' }}>
            Nothing scheduled. Add one-off client tasks on the Custom Projects page.
          </div>
        ) : (
          bucketed.map(b => (
            <div key={b.name} style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', color: BUCKET_COLOR[b.name], marginBottom: 8 }}>{b.name}</div>
              {b.groups.map(g => (
                <div key={g.key} style={{ padding: '10px 12px', borderRadius: 8, background: 'var(--bg-primary)', border: '1px solid var(--border)', marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{g.title}</span>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{fmtDueShort(g.due)}</span>
                  </div>
                  {g.notes && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>{g.notes}</div>}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                    {g.items.map(t => (
                      <label key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-primary)', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 20, padding: '4px 12px', cursor: 'pointer' }}>
                        <input type="checkbox" checked={false} onChange={() => clearScheduled(t.id)} style={{ cursor: 'pointer', accentColor: '#16a34a' }} />
                        {t.clientName || 'Client'}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))
        )}
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: 760 }}>
            {/* Header row */}
            <div style={{ display: 'grid', gridTemplateColumns: GRID, padding: '12px 20px', borderBottom: '1px solid var(--border)', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.5px' }}>
              <span>Client</span>
              {SERVICES.map(s => (
                <span key={s.key} style={{ textAlign: 'center' }}>
                  {s.label}
                  <span style={{ display: 'block', fontWeight: 500, color: 'var(--text-muted)', textTransform: 'none', letterSpacing: 0, marginTop: 2 }}>
                    {colCount(s)} / {colTotal(s)}
                  </span>
                </span>
              ))}
            </div>

            {clients.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted)', fontSize: 13 }}>No active clients.</div>
            ) : (
              clients.map((c, i) => {
                const applicable = SERVICES.filter(s => hasService(c, s.profile));
                const allDone = applicable.length > 0 && applicable.every(s => isDone(c.id, s.key));
                return (
                  <div
                    key={c.id}
                    style={{ display: 'grid', gridTemplateColumns: GRID, alignItems: 'center', padding: '10px 20px', borderTop: i === 0 ? 'none' : '1px solid var(--border)', background: allDone ? 'rgba(22,163,74,.06)' : 'transparent' }}
                  >
                    <Link
                      to={`/clients/${c.id}`}
                      style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 8 }}
                    >
                      {c.businessName}
                    </Link>
                    {SERVICES.map(s => {
                      const enabled = hasService(c, s.profile);
                      const done = isDone(c.id, s.key);
                      const stamp = done ? fmtStamp(status[c.id]?.[s.key]?.at) : '';
                      const cellId = `${c.id}:${s.key}`;
                      // Client hasn't signed up for this service — gray it out.
                      if (!enabled) {
                        return (
                          <div
                            key={s.key}
                            title="Not part of this client's plan"
                            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, opacity: 0.3 }}
                          >
                            <input type="checkbox" checked={false} disabled readOnly style={{ width: 17, height: 17, cursor: 'not-allowed' }} />
                            <span style={{ minHeight: 12, lineHeight: '12px' }} />
                          </div>
                        );
                      }
                      return (
                        <div key={s.key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                          <input
                            type="checkbox"
                            checked={done}
                            disabled={savingCell === cellId}
                            onChange={() => toggle(c.id, s.key)}
                            style={{ width: 17, height: 17, cursor: 'pointer', accentColor: '#16a34a' }}
                          />
                          <span style={{ fontSize: 10, color: 'var(--text-muted)', minHeight: 12, lineHeight: '12px', textAlign: 'center' }}>
                            {stamp}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 12, lineHeight: 1.6 }}>
        Check a box when that service is finished for the client — a timestamp appears once checked. Use the month dropdown to review or update past months. Click a client name to open their profile.
      </div>
    </div>
  );
}
